<template>
    <div class="home-particular-mobile-header-wrapper" :class="getAppThemeMode">
        <!-- Loader Generic -->
        <loaders-generic v-if="showAccountsLoader" fullSize/>
        <!-- Slider -->
        <slider-generic
        v-if="!showAccountsLoader && !errorState && getAccountsList.length && getMobileAppState && !applyAccountsErrorState"
        :selectedIndex="selectedAccountIndex"
        :sliderList="getAccountsList"
        @updateSelectedIndex="selectedAccountIndex = $event">
            <div
            v-for="account in getAccountsList"
            :key="`accoundCard${account.id}`"
            class="account-slider-item">
                <div v-if="account.description" class="account-description">{{account.description}}</div>
                <div v-else class="account-number" v-mask="ACCOUNT_NUMBER">{{account.accountNumber}}</div>
                <div v-if="account.iban" class="account-iban">
                    <div class="number" v-mask="IBAN_HOME">{{account.iban || '-'}}</div>
                    <button-quaternary icon="copy" @clicked="copyToClipboard(account.iban)"/>
                </div>
                <div class="amount-wrapper">
                    <!-- Show/Hide Amounts -->
                    <button-quaternary
                    :icon="hideAmounts ? 'eye-open' : 'eye-close'"
                    @clicked="[hideAmounts = !hideAmounts, emit('hideAmounts', hideAmounts)]"/>
                    <!-- Amount -->
                    <div class="amount">{{amountFormat(hideAmounts ? '***,00' : account.balance, account.currency)}}</div>
                </div>
            </div>
        </slider-generic>
        <empty-state-error v-if="applyAccountsErrorState" :message="t('home.errorMessage')"/>
    </div>

    <!-- Feedback Toast -->
    <toast-generic
    v-if="showToast"
    :feedbackText="feedbackToastData.text"
    :toastType="feedbackToastData.toastType"
    @hideMe="showToast = false"/>
</template>

<script setup>
import LoadersGeneric from '@/components/generic/loaders/Generic'
import SliderGeneric from '@/components/generic/sliders/Generic'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'

/* Vue */
import { ref, watch } from 'vue'

/* Assets */
import { ACCOUNT_NUMBER, IBAN_HOME } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'
import { sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideAmounts', 'updateSelectedAccount'])

/* Props
@applyAccountsErrorState {Boolean} prop to tell widget patrimony fetch state
*/
const props = defineProps({
    showAccountsLoader: Boolean,
    applyAccountsErrorState: Boolean
})

/* mapActions & mapGetters */
const { getAccountsList } = mapGetters(moduleMapper.HOME)
const { getMobileAppState, getAppThemeMode } = mapGetters()

const selectedAccountIndex = ref(0)
const hideAmounts = ref(true)
emit('hideAmounts', true)

watch(() => props.showAccountsLoader, () => {
    if (!props.showAccountsLoader && getAccountsList.value.length) {
        emit('updateSelectedAccount', getAccountsList.value[selectedAccountIndex.value].id)
    }
})

watch(() => selectedAccountIndex.value, () => {
    emit('updateSelectedAccount', getAccountsList.value[selectedAccountIndex.value].id)
})

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const copyToClipboard = (iban) => {
    const copyText = iban

    /** Copy content */
    if (window.cordova && window.cordova.plugins.clipboard) copyLink(copyText)
    else {
        navigator.clipboard.writeText(copyText)
        /** Show Toast */
        feedbackToastData.value = sucessObjectConstrutor(t('toast.successCopy', { label: 'IBAN' }))
        showToast.value = true
    }
}

const copyLink = (copyText) => {
    window.cordova.plugins.clipboard.copy(copyText)

    /** Show Toast */
    feedbackToastData.value = sucessObjectConstrutor(t('toast.successCopy', { label: 'IBAN' }))
    showToast.value = true
}
</script>

<style lang="scss" scoped>
.home-particular-mobile-header-wrapper {
    width: 100%;
    height: 140px;
    margin: 0 auto;

    display: flex;
    flex-direction: column;
    align-items: center;

    .slider-content-wrapper {

        :deep(.slider-wrapper) {
            padding-left: 0;
        }

        :deep(.circles-wrapper) {
            .circle:not(.selected) {
                background-color: var(--secondary-light-4);
                opacity: 0.64;
            }
        }
    }

    .account-slider-item {
        width: calc(100% + 32px);
        height: 72px;

        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;

        .account-number,
        .account-description {
            @include H6-Medium;
            color: var(--secondary-light-4);
        }

        .account-iban {
            width: auto;
            height: auto;
            margin-top: 4px;

            @include H6-Regular;
            color: var(--secondary-light-1);

            position: relative;

            :deep(.button-quaternary) {
                position: absolute;
                top: 50%;
                right: -40px;

                transform: translate(0, -50%);

                svg .change-color-fill {
                    fill: var(--secondary-light-4);
                }
            }
        }

        .amount-wrapper {
            margin-top: 8px;

            display: flex;
            align-items: center;
            justify-content: center;

            :deep(.button-quaternary) {
                svg .change-color-fill {
                    fill: var(--secondary-light-4);
                }
            }

            .amount {
                @include H3-Medium;
                color: var(--secondary-light-4);
            }
        }
    }

    &.dark {
        .slider-content-wrapper {
            :deep(.circles-wrapper) {
                .circle:not(.selected) {
                    background-color: var(--secondary-dark-4);
                }
            }
        }

        .account-slider-item {
            .account-number,
            .account-description {
                color: var(--secondary-dark-4);
            }

            .account-iban {
                color: var(--secondary-dark-1);

                :deep(.button-quaternary) {
                    svg .change-color-fill {
                        fill: var(--secondary-dark-4);
                    }
                }
            }

            .amount-wrapper {
                :deep(.button-quaternary) {
                    svg .change-color-fill {
                        fill: var(--secondary-dark-4);
                    }
                }

                .amount {
                    color: var(--secondary-dark-4);
                }
            }
        }
    }
}
</style>
